import { ChevronDown, ChevronUp, CheckCircle, AlertCircle, Clock, Zap } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card'
import { Badge } from '@components/ui/Badge'
import {
  runInsetCardClassName,
  runMutedPanelClassName,
  runSurfaceClassName,
} from '@/components/operate/RunChrome'
import { cn } from '@/utils/cn'
import type { ExecutionStep } from '@/store/operate'

interface ExecutionPanelProps {
  isOpen: boolean
  onToggle: () => void
  steps: ExecutionStep[]
  totalDuration?: number
  currentProgress?: number
}

export default function ExecutionPanel({
  isOpen,
  onToggle,
  steps,
  totalDuration = 0,
  currentProgress = 0,
}: ExecutionPanelProps) {
  const { t } = useTranslation()
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set())

  const toggleStepExpand = (stepId: string) => {
    const newExpanded = new Set(expandedSteps)
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId)
    } else {
      newExpanded.add(stepId)
    }
    setExpandedSteps(newExpanded)
  }

  const completedCount = steps.filter((s) => s.status === 'completed').length
  const failedCount = steps.filter((s) => s.status === 'failed').length
  const isRunning = steps.some((s) => s.status === 'running')
  const derivedProgress =
    steps.length === 0 ? 0 : (completedCount / steps.length) * 100
  const progressValue =
    currentProgress > 0 ? Math.max(currentProgress, derivedProgress) : derivedProgress
  const clampedProgressValue = Math.max(0, Math.min(100, Math.round(progressValue)))

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-success" size={16} />
      case 'failed':
        return <AlertCircle className="text-error" size={16} />
      case 'running':
        return <Zap className="text-warning animate-pulse" size={16} />
      default:
        return <Clock className="text-muted-foreground" size={16} />
    }
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  const getDuration = (step: ExecutionStep) => {
    if (!step.startedAt) return null
    const end = step.completedAt || new Date()
    return end.getTime() - step.startedAt.getTime()
  }

  return (
    <Card className={cn('flex h-full flex-col border-none shadow-none', runSurfaceClassName)}>
      <CardHeader className="border-b border-border pb-3">
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-between rounded-2xl px-3 py-2 text-left transition-colors hover:bg-card"
        >
          <CardTitle className="text-sm">{t('executionPanel.title')}</CardTitle>
          {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>
      </CardHeader>

      {isOpen && (
        <CardContent className="flex-1 space-y-4 overflow-y-auto p-4">
          {steps.length === 0 ? (
            <div className={`${runMutedPanelClassName} space-y-2`}>
              <p className="text-sm font-medium">{t('executionPanel.empty')}</p>
              <p className="text-xs text-muted-foreground">{t('executionPanel.summary')}</p>
            </div>
          ) : (
            <>
              <div className={cn(runInsetCardClassName, 'space-y-3')}>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {t('executionPanel.completedCount', { done: completedCount, total: steps.length })}
                  </span>
                  {isRunning && (
                    <span className="font-medium text-warning animate-pulse " role="status" aria-live="polite">
                      {t('executionPanel.running')}
                    </span>
                  )}
                </div>

                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-label={t('executionPanel.overallProgress')}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={clampedProgressValue}
                >
                  <div
                    className={cn(
                      'h-full transition-all duration-300',
                      failedCount > 0 ? 'bg-error' : 'bg-success',
                    )}
                    style={{
                      width: `${clampedProgressValue}%`,
                    }}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  {totalDuration > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {formatDuration(totalDuration)}
                    </Badge>
                  )}
                  {completedCount > 0 && (
                    <Badge variant="outline" className="text-xs text-success">
                      {completedCount} ✓
                    </Badge>
                  )}
                  {failedCount > 0 && (
                    <Badge variant="outline" className="text-xs text-error">
                      {failedCount} ✕
                    </Badge>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {steps.map((step, index) => {
                  const isExpanded = expandedSteps.has(step.id)
                  const duration = getDuration(step)

                  return (
                    <div
                      key={step.id}
                      className="overflow-hidden rounded-2xl border border-border bg-card"
                    >
                      <button
                        onClick={() => toggleStepExpand(step.id)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-card"
                      >
                        <div className="flex-shrink-0">
                          {getStatusIcon(step.status)}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {index + 1}. {step.name}
                          </div>
                        </div>

                        <div className="flex flex-shrink-0 items-center gap-2">
                          {duration && (
                            <span className="text-xs text-muted-foreground">
                              {formatDuration(duration)}
                            </span>
                          )}
                          {(step.error || step.summary) && (
                            <ChevronDown
                              size={16}
                              className={cn(
                                'transition-transform',
                                isExpanded && 'rotate-180',
                              )}
                            />
                          )}
                        </div>
                      </button>

                      {isExpanded && (step.error || step.summary) && (
                        <div className="border-t border-border bg-card px-4 py-3">
                          {step.summary && (
                            <div className="mb-2 break-words text-xs text-muted-foreground">
                              {step.summary}
                            </div>
                          )}
                          {typeof step.progress === 'number' && Number.isFinite(step.progress) && (
                            <div className="mb-2">
                              <div className="mb-1 flex justify-between text-[11px] text-muted-foreground">
                                <span>{t('executionPanel.progress')}</span>
                                <span>{step.progress}%</span>
                              </div>
                              <div
                                className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                                role="progressbar"
                                aria-label={t('executionPanel.stepProgress', { step: step.name })}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={Math.max(0, Math.min(100, Math.round(step.progress)))}
                              >
                                <div
                                  className="h-full rounded-full bg-primary transition-all"
                                  style={{ width: `${Math.max(0, Math.min(100, step.progress))}%` }}
                                />
                              </div>
                            </div>
                          )}
                          {step.error && (
                            <div className="break-words rounded bg-error-soft px-2 py-2 font-mono text-xs text-error">
                              {step.error}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  )
}
